import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, LayoutGrid, Store } from 'lucide-react';
import useNavigationStore from '../../store/navigationStore';

const MODULE_NAMES = {
  pos: 'Bán hàng',
  inventory: 'Kho hàng',
  hr: 'Nhân sự',
  marketing: 'Tiếp thị',
  finance: 'Tài chính',
  analytics: 'Báo cáo',
  settings: 'Hệ thống',
  kitchen: 'Bếp',
  crm: 'Khách hàng'
};

/**
 * GlobalHeader – Grid OS Double-Action Header
 * Back: quay lại tab trước đó trong history stack
 * Hub: reset về Grid Launcher (trang chủ)
 */
export default function GlobalHeader({ brandInfo }) {
  const { activeTab, isGridView, goBack, resetToHub, history } = useNavigationStore();

  const brandName = brandInfo?.brandName || brandInfo?.name || 'FnB AI Pro';
  const displayTabName = MODULE_NAMES[activeTab] || activeTab;

  const [isOnline, setIsOnline] = React.useState(typeof navigator !== 'undefined' ? navigator.onLine : true);

  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Ẩn header khi đang ở trang chủ Grid (Chỉ được return sau khi đã gọi hết Hooks)
  if (isGridView) return null;

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed top-0 left-0 right-0 z-40 h-16 flex items-center px-4 md:px-8 bg-slate-950/80 backdrop-blur-xl border-b border-white/5"
    >
      {/* Left: Back Button & Module Indicator */}
      <div className="flex-1 flex justify-start items-center gap-3">
        {history.length > 0 && (
          <button
            onClick={goBack}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5 transition-all active:scale-95"
            title="Quay lại"
          >
            <ArrowLeft size={18} />
            <span className="text-xs font-bold uppercase tracking-widest hidden md:inline">
              Quay lại
            </span>
          </button>
        )}

        {/* Current Module Indicator */}
        {activeTab && (
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 bg-indigo-500 rounded-full" />
            <span className="text-xs font-black text-indigo-400 uppercase tracking-widest truncate max-w-[80px] sm:max-w-none">
              {displayTabName}
            </span>
          </div>
        )}
      </div>

      {/* Center: Brand Name & Connection Status */}
      <div className="flex-shrink-0 flex flex-col items-center gap-0.5 px-2">
        <div className="flex items-center gap-2">
          <Store size={14} className="text-slate-500 hidden sm:block" />
          <span className="text-sm font-bold text-slate-300 tracking-tight truncate max-w-[120px] sm:max-w-[200px]">
            {brandName}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${isOnline ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.5)]'}`} />
          <span className={`text-[10px] font-bold uppercase tracking-[0.15em] ${isOnline ? 'text-emerald-500/80' : 'text-rose-500'}`}>
            {isOnline ? 'Hệ thống Trực tuyến' : 'Chế độ Ngoại tuyến'}
          </span>
        </div>
      </div>

      {/* Right: Hub Button */}
      <div className="flex-1 flex justify-end items-center">
        <button
          onClick={resetToHub}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-slate-400 hover:text-white hover:bg-indigo-600/20 hover:border-indigo-500/30 transition-all active:scale-95"
          title="Về trang chủ"
        >
          <LayoutGrid size={16} />
          <span className="text-xs font-bold uppercase tracking-widest hidden md:inline">Hub</span>
        </button>
      </div>
    </motion.header>
  );
}
